import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";

import clsx from "clsx";
import styles from "./Header.module.css";

import { AttendResponse, BasicBlock } from "../../../types/block/enum";
import { getBlockTypeFlags } from "../../../utils/common/block/getBlockTypeFlags";
import BackIcon from "../../../compoenets/atom/icons/BackIcon";
import { useIsMobileDevice } from "../../../hooks/mobile/useIsMobileDevice";
import useUpdateBlockCalendar from "../../../react-query/block/useUpdateBlockCalendar";
import { useFetchUserCalendarsQuery } from "../../../react-query/userCalendars/queries";
import { UserCalendarAccessType } from "../../../types/userCalendars/enum";
import { creatingCalendarBlockState } from "../../../recoil/calendar/creatingCalendarBlockState";
import { juneTrack } from "../../../utils/june/analytics";
import { JUNE_EVENT } from "../../../hooks/june/juneEvent";
import { getIsGooglePrimaryCalendar } from "../../../services/block/block.service";
import TaskEventToggle from "./TaskEventToggle";
import TaskDetailViewToggle from "../../../compoenets/organism/TaskDetail/Header/TaskDetailViewToggle";
import HeaderMenu from "../../../compoenets/organism/TaskDetail/Header/HeaderMenu";

function NoteModalHeader({ setIsModalNoteClicked }) {
  const taskDetail = useRecoilValue(taskPopupState);
  return (
    <div
      className={styles["note-modal-header"]}
      style={{ display: "flex", color: "white" }}
    >
      <div
        className={styles.backIcon}
        onClick={(e) => setIsModalNoteClicked(false)}
      >
        <BackIcon color={"#CACDCE"} />
      </div>
      <div className={styles["note-modal-header__title"]}>
        <span>
          {taskDetail.data.title ? taskDetail.data.title : `New ${taskDetail.data.blockType}`}
        </span>
      </div>
    </div>
  );
}

function Header({
  moreModalRef,
  visibilityDropdownRef,
  priorityDropdownRef,
  calendarDropdownRef,
  toggleExpand,
  setToggleExpand,
  isDisabled,
  selectedVisibilityType,
  isModalNoteClicked,
  currentRSVPValue,
  setIsModalNoteClicked,
  onChangePriority,
  handleVisibilityDropdownItemClick,
  handleTaskDelete,
  handleChangeRSVP,
}) {
  const fetchEditableUserCalendarsQuery = useFetchUserCalendarsQuery({
    calendarAccessType: UserCalendarAccessType.EDITABLE,
  });
  const { data: calendarsData } = useFetchUserCalendarsQuery();
  const updateBlockCalendar = useUpdateBlockCalendar();

  const setCreatingCalendarBlock = useSetRecoilState(creatingCalendarBlockState);
  const [taskDetail, setTaskDetail] = useRecoilState(taskPopupState);

  const { isTask, isEvent } = getBlockTypeFlags(taskDetail.data.blockType);

  // inbox general 블록은 calendarId가 없어야하는데 현재 백엔드에서 처리가 어려워서 프론트에서 예외처리 추가
  const isInboxGeneralBlock = useMemo(
    () => !taskDetail.data.start || !taskDetail.data.end,
    // NOTE: 초기 렌더링 이후 변경되지 않아야하기 때문에 deps에 빈 배열 추가
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const primaryCalendar = calendarsData?.find((calendar) => calendar.primary);
  const getCurrentCalendar = useCallback(
    (calendarId) => {
      return calendarsData?.find((calendar) => calendar.id === calendarId) ?? primaryCalendar;
    },
    [calendarsData, primaryCalendar]
  );

  const isShowUserCalendar = useMemo(() => {
    const hasCalendar = Boolean(
      fetchEditableUserCalendarsQuery.data && fetchEditableUserCalendarsQuery.data.length > 0
    );

    const hasBlockCalendar = Boolean(taskDetail.data?.calendarId);

    return hasCalendar && hasBlockCalendar && !isInboxGeneralBlock;
  }, [fetchEditableUserCalendarsQuery.data, isInboxGeneralBlock]);

  const onDataDuplicate = (e) => {
    taskDetail.handleDataDuplicate(e, taskDetail.data);
  };

  const updateAttendeesOnCalendarChange = ({ currentAttendees, newCalendar, isNewBlock }) => {
    if (!currentAttendees) return [];

    const isGooglePrimary = getIsGooglePrimaryCalendar({ calendar: newCalendar });
    const newOwnerEmail = newCalendar?.gcalId;
    if (!newOwnerEmail) return currentAttendees;

    if (!isNewBlock) {
      // 기존에 존재하던 블록의 경우에는 게스트 목록은 그대로 유지하고, 캘린더 주인이 아닐 경우 organizer/self제거
      const updatedAttendees = currentAttendees.map((attendee) => {
        const isNewOwner = attendee.email === newOwnerEmail;
        const updatedAttendee = { ...attendee };

        if (isGooglePrimary && isNewOwner) {
          // 새 캘린더의 소유자라면 organizer/self로 설정
          updatedAttendee.organizer = true;
          updatedAttendee.self = true;
          updatedAttendee.responseStatus =
            updatedAttendee.responseStatus ?? AttendResponse.ACCEPTED;
        } else if (!isNewOwner) {
          // delete organizer/self
          delete updatedAttendee.organizer;
          delete updatedAttendee.self;
        } else {
          // 기존 organizer/self는 제거
          delete updatedAttendee.organizer;
          delete updatedAttendee.self;
        }

        return updatedAttendee;
      });

      return updatedAttendees;
    }

    // 여기서부터 isNewBlock
    // 새로 생성되는 블록의 경우 이전 organizer를 아예 게스트 목록에서 제거하고
    // 새로운 캘린더 주인을 게스트로 추가 또는 이미 게스트에 있다면 self/organizer로 설정
    const updatedAttendees = currentAttendees
      .filter((attendee) => {
        // 생성중인 블록 + 이전 캘린더 attendees 중 organizer/self는 삭제 대상
        if (attendee.organizer && attendee.self) {
          return false; // 제거
        }
        return true;
      })
      .map((attendee) => {
        const isNewOwner = attendee.email === newOwnerEmail;
        const updatedAttendee = { ...attendee };

        if (isGooglePrimary && isNewOwner) {
          updatedAttendee.organizer = true;
          updatedAttendee.self = true;
          updatedAttendee.responseStatus =
            updatedAttendee.responseStatus ?? AttendResponse.ACCEPTED;
        } else if (attendee.organizer || attendee.self) {
          // 이전 organizer/self만 제거
          delete updatedAttendee.organizer;
          delete updatedAttendee.self;
        }

        return updatedAttendee;
      });

    // NOTE 블록 생성 시 organizer,self가 없는 경우 추가하는 로직
    const hasNewOwner = updatedAttendees.some((att) => att.email === newOwnerEmail);

    if (isNewBlock && isGooglePrimary && !hasNewOwner) {
      updatedAttendees.push({
        email: newOwnerEmail,
        organizer: true,
        self: true,
        responseStatus: AttendResponse.ACCEPTED,
      });
    }

    return updatedAttendees;
  };

  const handleMoveCalendar = (calendarId) => {
    // 캘린더 변경 시 게스트가 있다면 게스트 업데이트
    const newCalendar = getCurrentCalendar(Number(calendarId));

    setTaskDetail((prev) => {
      const updatedAttendees = updateAttendeesOnCalendarChange({
        currentAttendees: taskDetail.data.attendees,
        newCalendar,
        isNewBlock: taskDetail.isNewBlock,
      });

      return {
        ...prev,
        data: {
          ...prev.data,
          attendees: updatedAttendees,
          calendarId: Number(calendarId),
        },
      };
    });

    // 생성인 경우에는 block만 업데이트하고, move api 호출하지 않도록 얼리 리턴
    if (taskDetail.isNewBlock) {
      setCreatingCalendarBlock((prev) => ({ ...prev, calendarId: Number(calendarId) }));
      return;
    }

    updateBlockCalendar.mutate(
      {
        id: taskDetail.data.id,
        payload: { calendarId: Number(calendarId) },
        prevData: taskDetail.data,
      },
      {
        onSuccess: () => {
          juneTrack(JUNE_EVENT.CHANGE_CALENDAR_BLOCK, { location: taskDetail.type });
        },
      }
    );
  };

  const isMobileDevice = useIsMobileDevice();

  const handleTaskEventToggle = useCallback(() => {
    if (taskDetail.type === "inbox") return;

    setTaskDetail((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        blockType: prev.data.blockType === BasicBlock.TASK ? BasicBlock.EVENT : BasicBlock.TASK,
      },
    }));
  }, [taskDetail.type, setTaskDetail]);

  return (
    <div
      className={clsx(styles.header, {
        [styles["header-mini"]]: !toggleExpand,
        [styles["header-expand"]]: toggleExpand,
      })}
    >
      <div className={styles["header-content"]}>
        {isModalNoteClicked ? (
          <NoteModalHeader
            setIsModalNoteClicked={setIsModalNoteClicked}
            toggleExpand={toggleExpand}
            setToggleExpand={setToggleExpand}
          />
        ) : (
          <>
            <div className={styles["header-left"]}>
              {taskDetail.type !== "inbox" && (
                <TaskEventToggle
                  isTask={isTask}
                  isEvent={isEvent}
                  onToggle={handleTaskEventToggle}
                  hasAttendees={Boolean(taskDetail.data?.attendees?.length)}
                />
              )}
            </div>

            <HeaderMenu
              toggleExpand={toggleExpand}
              isEvent={isEvent}
              isTask={isTask}
              taskDetail={taskDetail}
              currentRSVPValue={currentRSVPValue}
              handleChangeRSVP={handleChangeRSVP}
              visibilityDropdownRef={visibilityDropdownRef}
              selectedVisibilityType={selectedVisibilityType}
              handleVisibilityDropdownItemClick={handleVisibilityDropdownItemClick}
              priorityDropdownRef={priorityDropdownRef}
              onChangePriority={onChangePriority}
              calendarDropdownRef={calendarDropdownRef}
              fetchEditableUserCalendarsQuery={fetchEditableUserCalendarsQuery}
              handleMoveCalendar={handleMoveCalendar}
              isDisabled={isDisabled}
              moreModalRef={moreModalRef}
              onDataDuplicate={onDataDuplicate}
              handleTaskDelete={handleTaskDelete}
              isShowUserCalendar={isShowUserCalendar}
              isInboxGeneralBlock={isInboxGeneralBlock}
            />
          </>
        )}
      </div>

      {!isMobileDevice && (
        <TaskDetailViewToggle
          toggleExpand={toggleExpand}
          setIsModalNoteClicked={setIsModalNoteClicked}
          setToggleExpand={setToggleExpand}
        />
      )}
    </div>
  );
}

export default Header;
